import { io } from 'socket.io-client';
import api from '../api';

let socket = null;
let authRetryTimer = null;

// socket.io never retries a handshake the server's auth middleware refused
// (expired or rotated token), so we retry it ourselves on this cadence.
const AUTH_RETRY_MS = 10000;

const getSocketUrl = () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl || apiUrl.startsWith('/')) return undefined;
    return apiUrl;
};

const retryAfterAuthRejection = (instance) => {
    clearTimeout(authRetryTimer);
    authRetryTimer = setTimeout(async () => {
        // Any authenticated call routes an expired token through api.js's
        // refresh / PIN / logout handling, so the retry handshakes with a live
        // token. A failure there is api.js's to surface; the handshake below
        // just fails again and re-arms this retry.
        await api.get('/auth/me').catch(() => {});
        if (socket === instance && !instance.connected) {
            instance.connect();
        }
    }, AUTH_RETRY_MS);
};

export const initializeSocket = () => {
    if (socket?.connected) {
        return socket;
    }

    const instance = io(getSocketUrl(), {
        // Read per handshake: /auth/refreshToken rotates the token in
        // localStorage only (state.token deliberately stays put), and the
        // server resolves the session by the exact token — a reconnect with
        // the login-time token is refused and the client stops receiving
        // playback events until a page reload.
        auth: (cb) => cb({ token: localStorage.getItem('token') }),
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
    });
    socket = instance;

    instance.on('connect', () => {
        console.log('Socket connected:', instance.id);
    });

    instance.on('disconnect', (reason) => {
        console.log('Socket disconnected:', reason);
    });

    instance.on('connect_error', (error) => {
        console.error('Socket connection error:', error.message);
        if (!instance.active) {
            retryAfterAuthRejection(instance);
        }
    });

    return instance;
};

export const getSocket = () => socket;

export const disconnectSocket = () => {
    clearTimeout(authRetryTimer);
    if (socket) {
        socket.disconnect();
        socket = null;
    }
};

export const requestCurrentState = () => {
    if (socket?.connected) {
        socket.emit('playback:request-state');
    }
};
